import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { useAuth } from '../../../context/AuthContext';
import { getCommissionsByRealtor } from '../../../services/commissionService';

const RewardStats = () => {
  const { userData } = useAuth();
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userData?.uid) {
      setLoading(false);
      return;
    }
    getCommissionsByRealtor(userData.uid)
      .then((rows) => setCommissions(rows || []))
      .catch(() => setCommissions([]))
      .finally(() => setLoading(false));
  }, [userData?.uid]);

  const chartData = useMemo(() => {
    const now = new Date();
    const months = [];
    const buckets = {};
    for (let i = 11; i >= 0; i -= 1) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = `${d.getMonth() + 1}월`;
      months.push({ key, label });
      buckets[key] = 0;
    }
    commissions.forEach((c) => {
      const ts = c.created_at;
      const d = ts?.toDate?.() || (ts?.seconds ? new Date(ts.seconds * 1000) : null);
      if (!d) return;
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (key in buckets) buckets[key] += c.amount || 0;
    });
    return months.map((m) => ({ name: m.label, value: buckets[m.key] || 0 }));
  }, [commissions]);

  const totalsByType = useMemo(() => {
    const acc = { settled: 0, conversion: 0, referral: 0, consulting: 0 };
    commissions.forEach((c) => {
      const amt = c.amount || 0;
      if (c.is_settled) acc.settled += amt;
      if (c.type === 'conversion') acc.conversion += amt;
      else if (c.type === 'referral') acc.referral += amt;
      else if (c.type === 'consulting') acc.consulting += amt;
    });
    return acc;
  }, [commissions]);

  return (
    <Container>
      <SectionLabel>월별 리워드</SectionLabel>
      <ChartCard>
        {loading ? (
          <EmptyChart>불러오는 중...</EmptyChart>
        ) : (
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#6B7280' }} interval={0} />
              <YAxis hide />
              <Tooltip
                cursor={{ fill: 'rgba(74, 108, 247, 0.08)' }}
                formatter={(v) => `${(v || 0).toLocaleString()}원`}
                labelFormatter={(l) => l}
              />
              <Bar dataKey="value" fill="#4A6CF7" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      <SectionLabel style={{ marginTop: 24 }}>유형별</SectionLabel>
      <TypeList>
        <TypeRow>
          <TypeName>실수익(전체완료)</TypeName>
          <TypeValue>{totalsByType.settled.toLocaleString()}원</TypeValue>
        </TypeRow>
        <TypeRow>
          <TypeName>상담요청(전환)</TypeName>
          <TypeValue>{totalsByType.conversion.toLocaleString()}원</TypeValue>
        </TypeRow>
        <TypeRow>
          <TypeName>추천</TypeName>
          <TypeValue>{totalsByType.referral.toLocaleString()}원</TypeValue>
        </TypeRow>
      </TypeList>
    </Container>
  );
};

export default RewardStats;

const Container = styled.div`
  padding-bottom: ${({ theme }) => theme.spacing.lg};
`;

const SectionLabel = styled.p`
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const ChartCard = styled.div`
  background: ${({ theme }) => theme.colors.bgWhite};
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  padding: ${({ theme }) => theme.spacing.lg} ${({ theme }) => theme.spacing.sm};
`;

const EmptyChart = styled.div`
  height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const TypeList = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const TypeRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.base} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const TypeName = styled.span`
  font-size: ${({ theme }) => theme.fontSize.base};
  color: ${({ theme }) => theme.colors.text};
`;

const TypeValue = styled.span`
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.primary};
`;
